import React from 'react';
import useSWR from 'swr';
import { useProject } from '../context/ProjectContext';
import { useLanguage } from '../context/LanguageContext';
import api, { fetcher } from '../services/api';
import { Layers3, Plus, Link as LinkIcon, Loader2, Trash2, X, Pipette } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import CustomDropdown from '../components/CustomDropdown';
import ConfirmationModal from '../components/ConfirmationModal';
import Skeleton from '../components/Skeleton';

const StatusPill = ({ status, t }) => {
  const isPending = status === 'pending';
  const label = isPending ? t('pending') : t('generated');
  return (
    <span
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
        isPending
          ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
          : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isPending ? 'bg-amber-400' : 'bg-emerald-400'} shadow-[0_0_10px_currentColor]`} />
      {label}
    </span>
  );
};

const ClaudeIcon = () => (
  <svg height="14" width="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flex: 'none' }}>
    <title>Claude</title>
    <path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128H9.2l-.79-.048-2.698-.073-2.339-.097-2.266-.122-.571-.121L0 11.784l.055-.352.48-.321.686.06 1.52.103 2.278.158 1.652.097 2.449.255h.389l.055-.157-.134-.098-.103-.097-2.358-1.596-2.552-1.688-1.336-.972-.724-.491-.364-.462-.158-1.008.656-.722.881.06.225.061.893.686 1.908 1.476 2.491 1.833.365.304.145-.103.019-.073-.164-.274-1.355-2.446-1.446-2.49-.644-1.032-.17-.619a2.97 2.97 0 01-.104-.729L6.283.134 6.696 0l.996.134.42.364.62 1.414 1.002 2.229 1.555 3.03.456.898.243.832.091.255h.158V9.01l.128-1.706.237-2.095.23-2.695.08-.76.376-.91.747-.492.584.28.48.685-.067.444-.286 1.851-.559 2.903-.364 1.942h.212l.243-.242.985-1.306 1.652-2.064.73-.82.85-.904.547-.431h1.033l.76 1.129-.34 1.166-1.064 1.347-.881 1.142-1.264 1.7-.79 1.36.073.11.188-.02 2.856-.606 1.543-.28 1.841-.315.833.388.091.395-.328.807-1.969.486-2.309.462-3.439.813-.042.03.049.061 1.549.146.662.036h1.622l3.02.225.79.522.474.638-.079.485-1.215.62-1.64-.389-3.829-.91-1.312-.329h-.182v.11l1.093 1.068 2.006 1.81 2.509 2.33.127.578-.322.455-.34-.049-2.205-1.657-.851-.747-1.926-1.62h-.128v.17l.444.649 2.345 3.521.122 1.08-.17.353-.608.213-.668-.122-1.374-1.925-1.415-2.167-1.143-1.943-.14.08-.674 7.254-.316.37-.729.28-.607-.461-.322-.747.322-1.476.389-1.924.315-1.53.286-1.9.17-.632-.012-.042-.14.018-1.434 1.967-2.18 2.945-1.726 1.845-.414.164-.717-.37.067-.662.401-.589 2.388-3.036 1.44-1.882.93-1.086-.006-.158h-.055L4.132 18.56l-1.13.146-.487-.456.061-.746.231-.243 1.908-1.312-.006.006z" fill="#D97757" fillRule="nonzero"></path>
  </svg>
);

const FigmaIcon = () => (
  <svg height="14" width="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flex: 'none' }}>
    <title>Figma</title>
    <path d="M4 20a4 4 0 014-4h4v4a4 4 0 01-8 0z" fill="#24CB71"></path>
    <path d="M12 0v8h4a4 4 0 000-8h-4z" fill="#FF7237"></path>
    <path d="M15.967 16a4 4 0 100-8 4 4 0 000 8z" fill="#00B6FF"></path>
    <path d="M4 4a4 4 0 004 4h4V0H8a4 4 0 00-4 4z" fill="#FF3737"></path>
    <path d="M4 12a4 4 0 004 4h4V8H8a4 4 0 00-4 4z" fill="#874FFF"></path>
  </svg>
);

const ComponentCardSkeleton = () => (
  <div className="rounded-[24px] border border-white/5 bg-white/[0.03] overflow-hidden">
    <div className="p-4 flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton width="140px" height="18px" borderRadius="0.5rem" />
        <div className="flex items-center gap-2">
          <Skeleton width="14px" height="14px" borderRadius="4px" />
          <Skeleton width="160px" height="12px" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Skeleton width="80px" height="24px" borderRadius="1rem" />
        <Skeleton width="36px" height="36px" borderRadius="0.75rem" />
      </div>
    </div>
    <div className="border-t border-white/5 bg-slate-950/30">
      <div className="h-44 w-full flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/10 border-t-transparent rounded-full animate-spin opacity-20" />
      </div>
    </div>
  </div>
);

const ComponentsPageSkeleton = () => (
  <div className="h-full">
    <div className="flex items-start justify-between gap-6 mb-8">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/5" />
        <div className="space-y-2">
          <Skeleton width="180px" height="32px" />
          <Skeleton width="320px" height="16px" />
        </div>
      </div>
    </div>

    <div className="grid grid-cols-1 gap-6">
      <div className="glass-panel border border-white/5 rounded-[28px] p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton width="120px" height="14px" />
          <Skeleton width="180px" height="36px" borderRadius="0.75rem" />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Skeleton width="60px" height="10px" />
            <Skeleton width="100%" height="48px" borderRadius="1rem" />
          </div>
          <div className="space-y-2">
            <Skeleton width="80px" height="10px" />
            <Skeleton width="100%" height="48px" borderRadius="1rem" />
          </div>
          <Skeleton width="100%" height="48px" borderRadius="1rem" />
        </div>
      </div>

      <div className="glass-panel border border-white/5 rounded-[28px] p-6 mb-12">
        <div className="flex items-center justify-between mb-8">
          <Skeleton width="120px" height="14px" />
          <Skeleton width="100px" height="14px" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <ComponentCardSkeleton key={i} />)}
        </div>
      </div>
    </div>
  </div>
);



const PreviewContainer = ({ id, componentType, t, primaryColor }) => {
  const [loaded, setLoaded] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const containerRef = React.useRef(null);
  const hex = (primaryColor || '#2563eb').replace(/^#/, '');
  const previewUrl = `${(api.defaults.baseURL?.replace(/\/$/, '') || '').replace(/\/api$/, '')}/preview/${id}?primary=${hex}`;

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  const renderSkeleton = () => {
    const type = (componentType || '').toLowerCase();
    
    if (type.includes('button') || type.includes('badge') || type.includes('pill')) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="relative">
            <Skeleton width="120px" height="40px" borderRadius="12px" className="opacity-50" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/40 animate-ping" />
            </div>
          </div>
        </div>
      );
    }
    
    if (type.includes('input') || type.includes('select') || type.includes('field') || type.includes('search')) {
      return (
        <div className="flex flex-col items-center justify-center h-full px-12 gap-3">
          <Skeleton width="100%" height="44px" borderRadius="12px" className="opacity-50" />
          <div className="flex justify-start w-full">
            <Skeleton width="40%" height="8px" borderRadius="4px" className="opacity-30" />
          </div>
        </div>
      );
    }

    if (type.includes('card') || type.includes('panel') || type.includes('modal') || type.includes('container')) {
      return (
        <div className="p-6 h-full space-y-4">
          <div className="flex items-center gap-3">
            <Skeleton width="32px" height="32px" borderRadius="8px" className="opacity-40" />
            <div className="space-y-2">
              <Skeleton width="100px" height="12px" borderRadius="4px" className="opacity-40" />
              <Skeleton width="60px" height="8px" borderRadius="4px" className="opacity-20" />
            </div>
          </div>
          <Skeleton width="100%" height="70px" borderRadius="12px" className="opacity-20" />
        </div>
      );
    }

    // Default stylized loader
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="relative">
          <div className="w-12 h-12 border-2 border-primary/10 border-t-primary rounded-full animate-spin" />
          <Layers3 className="absolute inset-0 m-auto text-primary/30 animate-pulse" size={16} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 animate-pulse">
            {t('preparingPreview') || 'Preparando Visualización'}
          </span>
          <span className="text-[9px] font-bold text-slate-600 uppercase tracking-widest">
            {componentType}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative w-full h-44 group overflow-hidden bg-slate-950/20">
      {!loaded && (
        <div className="absolute inset-0 z-20 bg-slate-950/40 backdrop-blur-[2px] transition-all duration-700">
          {renderSkeleton()}
        </div>
      )}
      {isVisible && (
        <iframe
          title={`${componentType} preview`}
          src={previewUrl}
          loading="lazy"
          className={`w-full h-44 transition-all duration-1000 ease-out border-0 ${
            loaded ? 'opacity-100 scale-100 blur-0' : 'opacity-0 scale-105 blur-md'
          }`}
          onLoad={() => setLoaded(true)}
        />
      )}
      
      {/* Subtle overlay reflection */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/0 via-white/[0.02] to-white/0 opacity-50" />
    </div>
  );
};


export default function Components() {

  const { t } = useLanguage();
  const { activeProjectId, userRole } = useProject();
  
  const [primaryColor, setPrimaryColor] = React.useState('#2563eb');
  const [showPicker, setShowPicker] = React.useState(false);
  const pickerRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  // Removed global variable injection to ensure theme only applies to component iframes
  // as per user request to isolate the playground from the main dashboard UI.
  /*
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', primaryColor);
    root.style.setProperty('--vg-primary', primaryColor);
    
    // Calculate foreground contrast
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    const foreground = brightness > 186 ? '#000000' : '#ffffff';
    
    root.style.setProperty('--color-primary-foreground', foreground);
    root.style.setProperty('--vg-primary-foreground', foreground);
    root.style.setProperty('--vg-primary-hover', `rgba(${r}, ${g}, ${b}, 0.8)`);
    root.style.setProperty('--vg-primary-light', `rgba(${r}, ${g}, ${b}, 0.1)`);
  }, [primaryColor]);
  */

  const [sourceType, setSourceType] = React.useState('figma'); // 'figma' or 'zip'
  const [figmaUrl, setFigmaUrl] = React.useState('');
  const [zipFile, setZipFile] = React.useState(null);
  const [componentType, setComponentType] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confModal, setConfModal] = React.useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'danger', confirmText: '' });
  const [deletingId, setDeletingId] = React.useState(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && file.name.endsWith('.zip')) {
      setZipFile(file);
    }
  };

  const specsKey = activeProjectId ? `/projects/${activeProjectId}/component-specs` : null;
  const listKey = activeProjectId ? `/projects/${activeProjectId}/components` : null;

  const { data: specsData, isLoading: specsLoading } = useSWR(specsKey, fetcher, { revalidateOnFocus: true });
  const { data: listData, isLoading: listLoading, mutate } = useSWR(listKey, fetcher, {
    revalidateOnFocus: true,
  });

  const isInitialLoading = (specsLoading && !specsData) || (listLoading && !listData);

  if (isInitialLoading) return <ComponentsPageSkeleton />;


  const specs = specsData?.data || [];
  const components = listData?.data || [];

  const canCreate = true;
  const canDelete = true;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!activeProjectId) return;

    if (sourceType === 'figma' && !figmaUrl.trim()) {
      setError(t('requiredFieldsMissing'));
      return;
    }
    if (sourceType === 'zip' && !zipFile) {
      setError(t('requiredFieldsMissing'));
      return;
    }
    if (!componentType) {
      setError(t('requiredFieldsMissing'));
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('componentType', componentType);
      
      if (sourceType === 'figma') {
        formData.append('figmaUrl', figmaUrl.trim());
      } else {
        formData.append('zipFile', zipFile);
      }

      await api.post(`/projects/${activeProjectId}/components`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFigmaUrl('');
      setZipFile(null);
      await mutate();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || t('toastError'));
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (component) => {
    if (!canDelete) return;
    setConfModal({
      isOpen: true,
      type: 'danger',
      title: t('confirmDeleteComponentTitle'),
      message: t('confirmDeleteComponentMsg', { name: component.componentType }),
      confirmText: t('delete'),
      onConfirm: async () => {
        try {
          setDeletingId(component._id);
          await api.delete(`/projects/${activeProjectId}/components/${component._id}`);
          await mutate();
        } catch (err) {
          setError(err?.response?.data?.message || err?.response?.data?.error || t('toastError'));
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  return (
    <div className="h-full">
      {/* Header needs relative z-index higher than the form below to prevent popovers from clipping */}
      <div className="relative z-[60] flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shadow-[0_0_30px_rgba(37,99,235,0.15)]">
              <Layers3 size={22} />
            </div>
            <div>
              <h1 className="text-3xl font-display font-black text-slate-100 italic">{t('components')}</h1>
              <p className="text-slate-500 text-sm font-medium mt-1">{t('componentsDesc')}</p>
            </div>
          </div>
        </div>
        
        {/* Theme Preview Selector */}
        <div className="flex items-center gap-2 lg:gap-3 bg-[#080808] px-4 py-2 lg:py-3 rounded-2xl border border-white/5">
          <div className="flex flex-col mr-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 line-clamp-1">{t('themePreview') || 'Theme Preview'}</span>
            <span className="text-[8px] font-bold uppercase text-primary tracking-widest">{t('live') || 'Live'}</span>
          </div>
          {['#2563eb', '#8b5cf6', '#10b981', '#f59e0b', '#e11d48', '#0ea5e9'].map(c => (
            <button
               key={c}
               type="button"
               title={c}
               onClick={() => setPrimaryColor(c)}
               className={`w-6 h-6 lg:w-7 lg:h-7 rounded-full border-2 transition-all duration-300 ${primaryColor === c ? 'border-white scale-110 shadow-[0_0_15px_var(--c)]' : 'border-transparent opacity-50 hover:opacity-100 hover:scale-105'}`}
               style={{ backgroundColor: c, '--c': c }}
            />
          ))}
          <div className="w-px h-6 bg-white/10 mx-1 lg:mx-2" />
          
          <div className="relative group/custom flex items-center justify-center" ref={pickerRef}>
            {/* Ambient glow matching the current hex */}
            <div 
              className="absolute inset-0 w-7 h-7 lg:w-8 lg:h-8 rounded-full blur-[10px] opacity-40 group-hover/custom:opacity-80 transition-opacity duration-300 pointer-events-none" 
              style={{ backgroundColor: primaryColor }} 
            />
            
            {/* Button to toggle Popover */}
            <button
              type="button"
              onClick={() => setShowPicker(!showPicker)}
              className="relative w-7 h-7 lg:w-8 lg:h-8 rounded-full border border-white/20 bg-slate-900 flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-110 hover:border-white/50 z-10 focus:outline-none"
              style={{ boxShadow: `inset 0 0 10px ${primaryColor}40` }}
            >
              <div className="absolute inset-0 opacity-20 transition-colors" style={{ backgroundColor: primaryColor }} />
              <Pipette size={14} className={`relative z-0 text-white drop-shadow-md transition-transform duration-300 ${showPicker ? '-rotate-12 scale-110 text-primary' : 'group-hover/custom:-rotate-12'}`} />
            </button>

            {/* Custom Premium Popover */}
            {showPicker && (
              <div className="absolute top-[120%] mt-2 right-0 z-[100] p-4 rounded-3xl bg-[#080808] shadow-2xl border border-white/10 origin-top-right animate-in fade-in zoom-in-95 duration-200" style={{ boxShadow: `0 20px 40px -10px ${primaryColor}25, inset 0 1px 0 rgba(255,255,255,0.05)` }}>
                <style>{`
                  .custom-picker.react-colorful { width: 220px; height: 220px; }
                  .custom-picker .react-colorful__pointer { width: 24px; height: 24px; border-radius: 50%; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.7); border: 2.5px solid white; cursor: grab; transition: transform 0.1s; }
                  .custom-picker .react-colorful__pointer:active { cursor: grabbing; transform: scale(1.1); }
                  .custom-picker .react-colorful__hue { height: 16px; border-radius: 8px; margin-top: 16px; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.1); }
                  .custom-picker .react-colorful__saturation { border-radius: 16px; border-bottom: none; box-shadow: inset 0 0 0 1px rgba(255,255,255,0.1); }
                `}</style>
                <div className="mb-5">
                  <HexColorPicker color={primaryColor} onChange={setPrimaryColor} className="custom-picker" />
                </div>
                <div className="flex items-center gap-3 px-4 py-3 bg-black/40 rounded-xl border border-white/5 focus-within:border-white/20 transition-colors">
                  <span className="text-slate-500 font-mono text-sm font-black">#</span>
                  <input 
                    type="text" 
                    value={primaryColor.replace('#', '')}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9A-Fa-f]/g, '').slice(0, 6);
                      setPrimaryColor('#' + val);
                    }}
                    className="w-full bg-transparent border-0 outline-none text-white text-sm font-black tracking-widest uppercase placeholder:text-slate-700"
                    placeholder="HEX"
                  />
                  <div className="w-5 h-5 rounded-md border border-white/10 shrink-0" style={{ backgroundColor: primaryColor }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create */}
      <div className="grid grid-cols-1 gap-6">
        <div className="relative z-50 glass-panel border border-white/5 rounded-[28px] p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm font-black text-white uppercase tracking-widest">{t('createComponent')}</div>
            
            <div className="flex p-1 bg-white/5 rounded-xl border border-white/5">
              <button
                type="button"
                onClick={() => setSourceType('figma')}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                  sourceType === 'figma' ? 'bg-primary text-slate-900 shadow-lg' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <FigmaIcon />
                {t('figmaLink')}
              </button>
              <button
                type="button"
                onClick={() => setSourceType('zip')}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${
                  sourceType === 'zip' ? 'bg-primary text-slate-900 shadow-lg' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <ClaudeIcon />
                {t('zipUpload')}
              </button>
            </div>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {sourceType === 'figma' ? (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('figmaUrl')}</label>
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/5 border border-white/5 focus-within:border-primary/30 transition-colors">
                  <LinkIcon size={16} className="text-slate-500" />
                  <input
                    value={figmaUrl}
                    onChange={(e) => setFigmaUrl(e.target.value)}
                    placeholder={t('figmaUrlPlaceholder')}
                    className="bg-transparent outline-none text-sm text-slate-200 placeholder:text-slate-600 w-full"
                    disabled={!canCreate}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('zipFile')}</label>
                <div 
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative flex flex-col items-center justify-center gap-4 px-6 py-10 rounded-[32px] border-2 border-dashed transition-all duration-500 cursor-pointer group ${
                    isDragging 
                      ? 'bg-primary/10 border-primary scale-[1.02] shadow-[0_0_50px_rgba(37,99,235,0.15)]' 
                      : (zipFile ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/5 border-white/10 hover:border-primary/30 hover:bg-white/[0.08]')
                  }`}
                >
                  <div className={`p-6 rounded-[24px] transition-all duration-500 ${
                    isDragging 
                      ? 'bg-primary text-white scale-110 shadow-xl shadow-primary/30 -translate-y-2' 
                      : (zipFile 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-white/5 text-slate-500 group-hover:text-primary group-hover:bg-primary/10 group-hover:-translate-y-1')
                  }`}>
                    <Layers3 size={40} className={isDragging ? 'animate-bounce' : ''} />
                  </div>
                  
                  <div className="text-center space-y-1">
                    <p className={`text-base font-display font-black italic transition-colors ${
                      isDragging ? 'text-primary' : (zipFile ? 'text-emerald-400' : 'text-slate-100')
                    }`}>
                      {isDragging ? t('dropHere') : (zipFile ? zipFile.name : t('dragDropInfo'))}
                    </p>
                    {!zipFile && !isDragging && (
                      <p className="text-[10px] uppercase font-black tracking-widest text-slate-500 group-hover:text-slate-400 transition-colors">
                        {t('orClickToBrowse')}
                      </p>
                    )}
                    {zipFile && !isDragging && (
                      <p className="text-[10px] uppercase font-black tracking-[0.2em] text-emerald-500/60">
                        {t('fileReady') || 'Archivo listo para subir'}
                      </p>
                    )}
                  </div>

                  <input
                    type="file"
                    accept=".zip"
                    onChange={(e) => setZipFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={!canCreate}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">{t('componentType')}</label>
              <CustomDropdown
                options={specs.map((s) => ({ value: s.componentType, label: s.componentType }))}
                value={componentType}
                onChange={setComponentType}
                placeholder={t('selectOption')}
                disabled={!canCreate || specsLoading}
                menuWidthPx={462}
                searchable={true}
                className="w-full"
              />
              <div className="text-[11px] text-slate-500 leading-relaxed">
                {specsLoading ? t('loading') : (
                  <>
                    <span className="font-black uppercase tracking-widest text-[10px] text-slate-600">{t('requiredProps')}</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(specs.find((s) => s.componentType === componentType)?.props || []).slice(0, 12).map((p) => (
                        <span key={p} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/5 text-[10px] font-bold text-slate-300">
                          {p}
                        </span>
                      ))}
                      {(specs.find((s) => s.componentType === componentType)?.props || []).length > 12 && (
                        <span className="px-2.5 py-1 rounded-full bg-white/5 border border-white/5 text-[10px] font-bold text-slate-500">
                          +{(specs.find((s) => s.componentType === componentType)?.props || []).length - 12} {t('more')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {error && (
              <div className="text-rose-400 text-xs font-bold bg-rose-500/10 border border-rose-500/20 rounded-2xl p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!canCreate || submitting || !componentType || (sourceType === 'figma' ? !figmaUrl.trim() : !zipFile)}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-primary text-slate-900 font-black text-sm shadow-[0_0_25px_rgba(37,99,235,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
              {t('create')}
            </button>
          </form>
        </div>

        {/* List */}
        <div className="relative z-10 glass-panel border border-white/5 rounded-[28px] p-6 overflow-hidden mb-12">
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm font-black text-white uppercase tracking-widest">{t('componentsList')}</div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {(listLoading || specsLoading) ? t('loading') : `${components.length} ${t('components')}`}
            </div>
          </div>

          {(listLoading || specsLoading) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(i => <ComponentCardSkeleton key={i} />)}
            </div>
          ) : components.length === 0 ? (
            <div className="text-slate-500 text-sm font-medium py-10 text-center">
              {t('noComponentsYet')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {components.map((c) => (
                <div key={c._id} className="rounded-[24px] border border-white/5 bg-white/[0.03] overflow-hidden">
                  <div className="p-4 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-slate-100 truncate">{c.componentType}</div>
                      {c.sourceType === 'zip' ? (
                        <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 max-w-full">
                          <Layers3 size={14} className="text-primary" />
                          <span className="truncate min-w-0 font-medium">{t('zipFile')}</span>
                        </div>
                      ) : (
                        <a
                          href={c.figmaUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-slate-500 hover:text-slate-300 transition-colors mt-1 flex items-center gap-2 max-w-full"
                          title={c.figmaUrl}
                        >
                          <LinkIcon size={14} />
                          <span className="truncate min-w-0">{c.figmaUrl}</span>
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusPill status={c.status} t={t} />
                      {canDelete && (
                        <button
                          onClick={() => askDelete(c)}
                          disabled={deletingId === c._id}
                          className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 hover:border-rose-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('delete')}
                        >
                          {deletingId === c._id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-white/5 bg-slate-950/30">
                    {c.status === 'generated' ? (
                      <PreviewContainer id={c._id} componentType={c.componentType} t={t} primaryColor={primaryColor} />
                    ) : (
                      <div className="h-44 flex items-center justify-center text-slate-600 text-xs font-bold">
                        {t('noPreview')}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        {...confModal}
        isOpen={confModal.isOpen}
        title={confModal.title}
        message={confModal.message}
        type={confModal.type}
        confirmText={confModal.confirmText}
        onConfirm={confModal.onConfirm || (() => {})}
        onClose={() => setConfModal({ ...confModal, isOpen: false })}
        t={t}
      />
    </div>
  );
}

